/**
 * DisputedPaymentCard — Shannon's per-dispute action card on the Payments tab.
 *
 * Shows the dispute reason, Stripe deadline, and three action buttons:
 *   • Respond with evidence  — opens the Stripe dispute in a new tab so
 *                              Shannon can attach evidence in the Stripe
 *                              dashboard. The actual API call to submit
 *                              evidence stays in Stripe, not duplicated here.
 *   • Accept + refund        — marks the deal_payment refunded in CRM.
 *                              Stripe-side refund is auto-issued by Stripe
 *                              on accept. (Oct 22, 2026: no QuickBooks credit
 *                              memo any more. quickbooks-sync has no such
 *                              action, and the browser never reached it.)
 *   • Escalate to Greg       — fires a notification + Bardin Team SMS so
 *                              Greg has visibility on the dispute.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 12, §10c #5)
 */

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { fmtUSD } from "../../lib/analytics";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import type { DealPayment } from "../../lib/types";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { useAuth } from "../../context/AuthContext";

interface DisputeMeta {
  reason?: string;
  due_by?: number;          // unix epoch seconds (Stripe's evidence_details.due_by)
  amount?: number;          // cents
}

export default function DisputedPaymentCard({
  payment,
  onResolved,
}: {
  payment: DealPayment;
  onResolved?: () => void;
}) {
  const { member } = useAuth();
  const [meta, setMeta] = useState<DisputeMeta | null>(null);
  const [hoName, setHoName] = useState<string>("(loading)");
  const [claim, setClaim] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Pull dispute detail from finance_audit_log (the stripe-webhook wrote
      // it on charge.dispute.created)
      const [{ data: aud }, { data: deal }] = await Promise.all([
        supabase
          .from("finance_audit_log")
          .select("after_jsonb")
          .eq("deal_id", payment.deal_id)
          .eq("action", "stripe_dispute_opened")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("deals")
          .select("homeowner_name, claim_number")
          .eq("id", payment.deal_id)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      if (aud?.after_jsonb) setMeta(aud.after_jsonb as DisputeMeta);
      if (deal) {
        setHoName(deal.homeowner_name ?? "(no name)");
        setClaim(deal.claim_number ?? null);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [payment.deal_id]);

  const dueBy = meta?.due_by ? new Date(meta.due_by * 1000) : null;
  const daysLeft = dueBy ? Math.max(0, Math.floor((dueBy.getTime() - Date.now()) / 86_400_000)) : null;
  const urgent = daysLeft != null && daysLeft <= 3;

  async function openInStripe() {
    if (!payment.stripe_charge_id) {
      alert("No Stripe charge ID on this payment — open Stripe Dashboard manually.");
      return;
    }
    // Stripe doesn't expose dispute IDs to the client by default; open the charge
    // page which lets Shannon click into the active dispute from there.
    window.open(`https://dashboard.stripe.com/payments/${payment.stripe_charge_id}`, "_blank", "noopener,noreferrer");
  }

  async function acceptAndRefund() {
    if (!confirm(`Accept the dispute and refund ${fmtUSD(payment.amount)} to the homeowner? This cannot be reversed.`)) return;
    setActing(true);
    setError(null);
    try {
      // actor_id references team_roster(id), not auth.users(id).
      const actorId = member?.id ?? null;

      // 1) Flip the deal_payment to refunded
      const { error: updateErr } = await supabase
        .from("deal_payments")
        .update({
          state: "refunded",
          stripe_refunded_at: new Date().toISOString(),
        })
        .eq("id", payment.id);
      if (updateErr) throw updateErr;

      // 2) Audit log
      await supabase.from("finance_audit_log").insert({
        deal_id: payment.deal_id,
        actor_id: actorId,
        action: "dispute_accepted_refund",
        is_financial: true,
        amount_delta: -Number(payment.amount),
        before_jsonb: { state: "disputed", amount: payment.amount },
        after_jsonb:  { state: "refunded", amount: payment.amount, stripe_charge_id: payment.stripe_charge_id },
        reason: "Shannon accepted Stripe dispute — full refund",
      });

      onResolved?.();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing(false);
    }
  }

  async function escalate() {
    setActing(true);
    setError(null);
    try {
      const actorId = member?.id ?? null;
      const { data: greg } = await supabase
        .from("team_roster")
        .select("id, phone")
        .eq("email", "greg@bardinroofing.com")
        .eq("is_active", true)
        .maybeSingle();
      if (!greg) throw new Error("Greg not found in team_roster");

      await supabase.from("notifications").insert({
        user_id: greg.id,
        type: "dispute_escalated",
        title: `🆘 Stripe dispute escalated — ${hoName}`,
        body: `${fmtUSD(payment.amount)} disputed${meta?.reason ? ` · "${meta.reason}"` : ""}${dueBy ? ` · due ${format(dueBy, "MMM d")}` : ""}`,
        deal_id: payment.deal_id,
        metadata: { url: "/finance?view=shannon&tab=payments&bucket=disputed", payment_id: payment.id },
      });

      if (greg.phone) {
        const e164 = "+1" + greg.phone.replace(/\D/g, "").slice(-10);
        await supabase.functions.invoke("twilio-send-sms", {
          body: {
            to: e164,
            body: `[Bardin Team] Stripe dispute escalated: ${hoName} ${fmtUSD(payment.amount)}${meta?.reason ? ` · ${meta.reason}` : ""}. Tap: app.bardinroofing.com/finance?view=shannon&tab=payments`,
            tag: "dispute_escalated",
          },
        }).catch((e) => console.warn("[escalate SMS] failed:", e));
      }

      await supabase.from("finance_audit_log").insert({
        deal_id: payment.deal_id,
        actor_id: actorId,
        action: "dispute_escalated",
        is_financial: true,
        after_jsonb: { stripe_charge_id: payment.stripe_charge_id },
        reason: "Shannon escalated Stripe dispute to Greg",
      });

      alert("✓ Greg notified.");
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing(false);
    }
  }

  return (
    <div className={"rounded-xl border-2 p-4 mb-2 " + (urgent ? "border-red-500 bg-red-50/50" : "border-red-300 bg-red-50/30")}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base">🆘</span>
            <span className="text-sm font-bold text-bardin-ink">{hoName}</span>
            {claim && <span className="text-xs text-slate-500">#{claim}</span>}
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-200 text-red-800">DISPUTED</span>
          </div>
          {meta?.reason && (
            <div className="text-xs text-slate-700 mt-1.5">
              <strong>Reason:</strong> <span className="italic">{meta.reason}</span>
            </div>
          )}
          {dueBy && (
            <div className={"text-xs mt-1 " + (urgent ? "text-red-700 font-semibold" : "text-slate-600")}>
              {urgent ? "⏰ " : ""}
              <strong>Evidence due:</strong> {format(dueBy, "MMM d, yyyy")} ({formatDistanceToNow(dueBy, { addSuffix: true })})
            </div>
          )}
          {!meta && payment.stripe_disputed_at && (
            <div className="text-xs text-slate-500 mt-1">
              Disputed {formatDistanceToNow(parseISO(payment.stripe_disputed_at), { addSuffix: true })}
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-lg font-extrabold text-red-700">{fmtUSD(payment.amount)}</div>
          <div className="text-[10px] text-slate-500">at risk</div>
        </div>
      </div>

      {error && <div className="text-xs text-red-700 bg-red-100 rounded px-2 py-1 mt-2">⚠ {error}</div>}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={openInStripe}
          disabled={acting}
          className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50"
        >
          📤 Respond in Stripe
        </button>
        <button
          onClick={acceptAndRefund}
          disabled={acting}
          className="px-3 py-1.5 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >
          {acting ? "Saving…" : "💸 Accept + refund"}
        </button>
        <button
          onClick={escalate}
          disabled={acting}
          className="px-3 py-1.5 text-xs font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
        >
          🚨 Escalate to Greg
        </button>
      </div>

      <div className="text-[10px] text-slate-500 mt-2">
        Stripe disputes have a 7-21 day response window depending on the reason code. Missing the deadline = automatic loss for Bardin.
      </div>
    </div>
  );
}
